import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Check, CalendarDays, ImageOff, Info, Pencil, X } from 'lucide-react';
import { OrderInformationSection } from './checkout/OrderInformationSection';
import { useAddressAutocomplete } from '../hooks/useAddressAutocomplete';
import { Modal } from './ui';
import {
  GiftRedemptionService,
  GiftRedemptionPageData,
  GiftRedemptionConfirmationData,
  GiftRedemptionProduct,
  GiftClaimDraftPayload,
  GiftAlreadyClaimedError,
} from '../services/giftRedemptionService';
import { ApiError } from '../errors/ApiError';

function productColors(product: GiftRedemptionProduct): string[] {
  return Array.from(new Set(product.variations.map((v) => v.color).filter(Boolean))) as string[];
}

function productSizes(product: GiftRedemptionProduct): string[] {
  return Array.from(new Set(product.variations.map((v) => v.size).filter(Boolean))) as string[];
}

function productColorHexByName(product: GiftRedemptionProduct): Map<string, string> {
  return new Map(product.variations.filter((v) => v.color && v.hex).map((v) => [v.color as string, v.hex as string]));
}

// Same helper ProductPage.tsx/QuickViewModal.tsx use to pick readable text over a color swatch.
function getTextColor(hexColor: string): string {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

function productHasVariations(product: GiftRedemptionProduct): boolean {
  return productColors(product).length > 0 || productSizes(product).length > 0;
}

// "Claim My Gift" experience reached via a signed emailed link (PT-2616). Deliberately
// self-contained - no ShoppingContext/CheckoutProvider, since this flow never touches the
// cart. Single page, always showing every gift at once - picking one never makes the grid
// disappear. A gift with color/size options opens a quick-pick modal (mirrors
// QuickViewModal's pattern elsewhere in this storefront) instead of pushing the grid out of
// the way; a gift with no options is selected immediately. Clicking an already-selected gift
// with options reopens the same modal, pre-filled with the current choice, to change it. A
// no-option gift goes through that same modal (to show its full description) only when
// there's more to see than the card already shows - a description, or a truncated name;
// otherwise it's selected immediately.
export function GiftRedemptionPage() {
  const { recipientId, programId, selfProgramId } = useParams<{
    recipientId?: string;
    programId?: string;
    selfProgramId?: string;
  }>();
  const location = useLocation();
  // Reached via nova-kit's "Preview" button (see ProgramHeader.vue) instead of an emailed
  // recipient link - same page, but nothing here is a real recipient, so autosave/submission
  // must both no-op instead of trying to persist against a claim that doesn't exist.
  const isPreview = Boolean(programId);
  // Reached via the self-service claim code (see GiftCodeEntryPage.tsx) - also no recipient yet,
  // but unlike preview this one is fully submittable and asks for the claimant's own email.
  const isSelfClaim = Boolean(selfProgramId);

  const [data, setData] = useState<GiftRedemptionPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<GiftRedemptionConfirmationData | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [previewBannerDismissed, setPreviewBannerDismissed] = useState(false);

  const [selectedProduct, setSelectedProduct] = useState<GiftRedemptionProduct | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);

  const [variationModalProduct, setVariationModalProduct] = useState<GiftRedemptionProduct | null>(null);
  const [modalColor, setModalColor] = useState<string | null>(null);
  const [modalSize, setModalSize] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalcode, setPostalcode] = useState('');
  const [phone, setPhone] = useState('');
  const [checkoutFieldValues, setCheckoutFieldValues] = useState<Record<string, string>>({});

  // A plain useRef's `.current` wouldn't reliably be populated yet the first time this render
  // reads it (assigning a ref doesn't itself trigger a re-render) - a callback ref feeding a
  // real state value is what ShippingAddressSection.tsx already uses for the same reason.
  const [address1El, setAddress1El] = useState<HTMLInputElement | null>(null);
  const address1Ref = useCallback((node: HTMLInputElement | null) => setAddress1El(node), []);

  // Whether a card's line-clamped name actually got cut off is only knowable from the DOM,
  // and re-measured on resize since a narrower grid column can start clamping it.
  const [truncatedNameProductIds, setTruncatedNameProductIds] = useState<ReadonlySet<number>>(() => new Set());
  const [productNameResizeObserver] = useState(
    () =>
      new ResizeObserver((entries) => {
        setTruncatedNameProductIds((previous) => {
          const next = new Set(previous);
          for (const entry of entries) {
            const nameElement = entry.target as HTMLElement;
            const productId = Number(nameElement.dataset.productId);
            if (nameElement.scrollHeight > nameElement.clientHeight) {
              next.add(productId);
            } else {
              next.delete(productId);
            }
          }
          const changed = next.size !== previous.size || [...next].some((productId) => !previous.has(productId));
          return changed ? next : previous;
        });
      }),
  );
  useEffect(() => () => productNameResizeObserver.disconnect(), [productNameResizeObserver]);
  const observeProductName = useCallback(
    (node: HTMLElement | null) => {
      if (node) productNameResizeObserver.observe(node);
    },
    [productNameResizeObserver],
  );

  useAddressAutocomplete(address1El, (parsed) => {
    setAddress1(parsed.address1);
    setCity(parsed.city);
    setState(parsed.state);
    setPostalcode(parsed.zip);
  });

  useEffect(() => {
    if (!recipientId && !programId && !selfProgramId) return;
    setLoading(true);
    const request = isSelfClaim
      ? GiftRedemptionService.resolveSelfClaimProgram(selfProgramId as string, location.search)
      : isPreview
        ? GiftRedemptionService.resolvePreview(programId as string, location.search)
        : GiftRedemptionService.resolve(recipientId as string, location.search);

    request
      .then((pageData) => {
        setData(pageData);
        setRecipientName(pageData.recipientName ?? '');
        setPhone(pageData.recipientPhone ?? '');

        // Resumes a prior visit's autosaved draft (see saveDraft() below) instead of starting
        // the form over blank. Only restores the product if it's still one of the currently
        // eligible products - the draft target may have gone out of stock/inactive since.
        const draftProduct = pageData.products.find((product) => product.id === pageData.draft.productId) ?? null;
        setSelectedProduct(draftProduct);
        if (draftProduct) {
          setSelectedColor(pageData.draft.color);
          setSelectedSize(pageData.draft.size);
        }
        setAddress1(pageData.draft.address1 ?? '');
        setAddress2(pageData.draft.address2 ?? '');
        setCity(pageData.draft.city ?? '');
        setState(pageData.draft.state ?? '');
        setPostalcode(pageData.draft.postalcode ?? '');
        setCheckoutFieldValues(pageData.draft.checkoutFieldValues ?? {});
      })
      .catch((err: unknown) => {
        if (err instanceof GiftAlreadyClaimedError) {
          // Signature is still valid (a truly expired/tampered link is a 404, handled below) -
          // show what they picked instead of just telling them they already claimed it.
          setRecipientName(err.confirmation.shippingAddress.name ?? '');
          setConfirmation(err.confirmation);
        } else if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError('This link is not valid or has expired.');
        }
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientId, programId, selfProgramId]);

  // Autosaves the recipient's in-progress selection, so an interrupted claim still leaves
  // something for an admin to see instead of nothing - a gift/variation pick saves right away,
  // typing into the shipping/contact fields saves on a debounce instead of firing a request
  // per keystroke. Neither ever fires before a gift is picked (the form below doesn't even
  // render until then), and a failed autosave is swallowed - it must never interrupt the
  // recipient's own flow the way a failed final submit does.
  const draftSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const buildDraftPayload = useCallback(
    (): GiftClaimDraftPayload => ({
      productId: selectedProduct?.id ?? null,
      color: selectedColor,
      size: selectedSize,
      recipientName: recipientName || null,
      address1: address1 || null,
      address2: address2 || null,
      city: city || null,
      state: state || null,
      postalcode: postalcode || null,
      countrycode: 'US',
      phone: phone || null,
      checkoutFieldValues,
    }),
    [selectedProduct, selectedColor, selectedSize, recipientName, address1, address2, city, state, postalcode, phone, checkoutFieldValues]
  );

  const saveDraft = useCallback(() => {
    if (!recipientId) return;
    GiftRedemptionService.saveDraft(recipientId, location.search, buildDraftPayload()).catch((draftError: unknown) => {
      console.warn('Failed to save gift claim draft:', draftError);
    });
  }, [recipientId, location.search, buildDraftPayload]);

  useEffect(() => {
    if (!selectedProduct) return;
    saveDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct, selectedColor, selectedSize]);

  useEffect(() => {
    if (!selectedProduct) return;

    if (draftSaveTimeoutRef.current) {
      clearTimeout(draftSaveTimeoutRef.current);
    }

    draftSaveTimeoutRef.current = setTimeout(saveDraft, 800);

    return () => {
      if (draftSaveTimeoutRef.current) {
        clearTimeout(draftSaveTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientName, address1, address2, city, state, postalcode, phone, checkoutFieldValues]);

  const modalColors = variationModalProduct ? productColors(variationModalProduct) : [];
  const modalSizes = variationModalProduct ? productSizes(variationModalProduct) : [];
  const modalColorHexByName = variationModalProduct ? productColorHexByName(variationModalProduct) : new Map<string, string>();
  const modalMissingColor = modalColors.length > 0 && !modalColor;
  const modalMissingSize = modalSizes.length > 0 && !modalSize;

  const selectionSummary = selectedProduct
    ? [selectedProduct.name, selectedColor, selectedSize].filter(Boolean).join(' · ')
    : null;

  // A no-option gift's modal only exists to show what its card can't - with no description
  // and a name that fits, there's nothing more to show, so no modal at all.
  function productHasHiddenDetails(product: GiftRedemptionProduct): boolean {
    return Boolean(product.description) || truncatedNameProductIds.has(product.id);
  }

  function handleProductClick(product: GiftRedemptionProduct) {
    if (productHasVariations(product) || productHasHiddenDetails(product)) {
      // Reopening the gift that's already selected pre-fills the modal with the current
      // choice, so "change" is the same modal, not a reset back to nothing picked.
      const isReopeningCurrentSelection = selectedProduct?.id === product.id;
      setVariationModalProduct(product);
      setModalColor(isReopeningCurrentSelection ? selectedColor : null);
      setModalSize(isReopeningCurrentSelection ? selectedSize : null);
      return;
    }

    setSelectedProduct(product);
    setSelectedColor(null);
    setSelectedSize(null);
  }

  function confirmVariationSelection() {
    if (!variationModalProduct || modalMissingColor || modalMissingSize) return;

    setSelectedProduct(variationModalProduct);
    setSelectedColor(modalColor);
    setSelectedSize(modalSize);
    setVariationModalProduct(null);
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if ((!recipientId && !isSelfClaim) || !selectedProduct) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      const payload = {
        productId: selectedProduct.id,
        color: selectedColor,
        size: selectedSize,
        recipientName,
        address1,
        address2: address2 || null,
        city,
        state: state || null,
        postalcode: postalcode || null,
        countrycode: 'US',
        phone,
        checkoutFieldValues,
      };
      const result = isSelfClaim
        ? await GiftRedemptionService.selfClaim(selfProgramId as string, location.search, { ...payload, email })
        : await GiftRedemptionService.claim(recipientId as string, location.search, payload);
      setConfirmation(result);
    } catch (err: unknown) {
      setSubmitError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <CenteredMessage>Loading your gift…</CenteredMessage>;
  }

  if (error) {
    return <CenteredMessage>{error}</CenteredMessage>;
  }

  // Already claimed (signature still valid, so this isn't the "expired/invalid" error case
  // above) - show their confirmation instead of just a "you already claimed" message. Checked
  // before the `!data` guard below since resolve() throws before ever setting `data` here.
  if (confirmation) {
    return <ConfirmationScreen confirmation={confirmation} recipientName={recipientName} />;
  }

  if (!data) {
    return null;
  }

  if (data.products.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center px-4 py-20">
          <div className="max-w-sm text-center">
            <h1 className="text-xl font-semibold text-gray-800 mb-2">Sorry, no gifts are available right now</h1>
            <p className="text-gray-500 text-sm">
              We couldn't find any gifts available for this program at the moment. Please check back soon, or reach
              out to your program administrator for help.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {isPreview && !previewBannerDismissed && (
        <div className="bg-amber-400 text-amber-950 text-xs sm:text-sm font-medium py-2 px-4 flex items-center justify-center gap-3">
          <span className="text-center">Preview mode - this is how recipients will see the claim page. Nothing here can be submitted.</span>
          <button
            type="button"
            onClick={() => setPreviewBannerDismissed(true)}
            aria-label="Dismiss preview banner"
            className="shrink-0 hover:opacity-70"
          >
            <X size={14} />
          </button>
        </div>
      )}
      <header className="bg-[#0b2c53] text-white px-4 sm:px-8 py-10 sm:py-16 text-center">
        {/* flex-col, not stacked block elements with margins - the badge/pill are inline-level
            boxes, so with no heading/subheading between them (both optional, see
            giftRedemptionService.ts) they'd otherwise flow onto the same line instead of each
            keeping its own row. */}
        <div className="max-w-2xl mx-auto flex flex-col items-center gap-4">
          {data.programName && (
            <span className="inline-block bg-white/10 text-[11px] font-semibold tracking-wider uppercase px-3 py-1 rounded-full">
              {data.programName}
            </span>
          )}
          {data.heading && <h1 className="text-2xl sm:text-4xl font-serif font-bold">{data.heading}</h1>}
          {data.subheading && (
            <div
              className="text-blue-100 text-sm sm:text-base max-w-md [&_a]:underline [&_ul]:list-disc [&_ul]:ml-5 [&_ol]:list-decimal [&_ol]:ml-5"
              dangerouslySetInnerHTML={{ __html: data.subheading }}
            />
          )}
          {data.shipByMessage && (
            <div className="inline-flex items-center gap-2 bg-white/10 rounded-full px-4 py-1.5 text-sm">
              <CalendarDays size={14} />
              Gifts ship by {data.shipByMessage}
            </div>
          )}
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 sm:px-8 pb-14">
        <div className="mt-8">
          <SectionHeader
            title="Select your gift"
            subtitle={data.products.length > 1 ? 'Choose one of the following items.' : undefined}
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            {data.products.map((product) => {
              const isSelected = selectedProduct?.id === product.id;
              const colors = productColors(product);
              const sizes = productSizes(product);
              const colorHexByName = productColorHexByName(product);
              const totalVariantCount = colors.length + sizes.length;
              // Real swatches only make sense when every color actually has a hex to paint -
              // otherwise they'd fall back to a meaningless gray dot. Also cap the swatch
              // count so a product with a long variant list doesn't turn into a wall of dots.
              const canShowSwatches = colors.length > 0 && colors.every((color) => colorHexByName.has(color)) && totalVariantCount <= 5;
              const hasDetailsModal = totalVariantCount > 0 || productHasHiddenDetails(product);
              // Clicking a selected card only does something (reopens the modal) when it has one -
              // and a not-yet-selected card should invite a first pick, not imply an existing
              // choice to change.
              const showSelectedHoverOverlay = isSelected && hasDetailsModal;
              return (
                <button
                  type="button"
                  key={product.id}
                  onClick={() => handleProductClick(product)}
                  title={
                    totalVariantCount > 0
                      ? isSelected
                        ? 'Click to change color/size'
                        : 'Click to choose color/size'
                      : hasDetailsModal
                        ? 'Click to view full details'
                        : undefined
                  }
                  className={`group relative bg-white border rounded-lg p-3 text-left transition-all hover:shadow-md ${
                    isSelected ? 'border-[#0b2c53] ring-2 ring-blue-100' : 'border-gray-200'
                  } ${
                    // The border/ring keep their width on hover either way, only the color
                    // changes - never toggling border-width or ring-width avoids the card
                    // nudging by a pixel. A selected card with a hover overlay lets both fade to
                    // transparent (the overlay already communicates "hovered"); other cards
                    // still darken their border on hover as before.
                    showSelectedHoverOverlay
                      ? 'hover:border-transparent hover:ring-transparent'
                      : 'hover:border-gray-300'
                  }`}
                >
                  {isSelected && (
                    <span className="absolute top-2 right-2 z-10 w-5 h-5 rounded-full bg-[#0b2c53] text-white flex items-center justify-center overflow-hidden">
                      <Check
                        size={12}
                        className={`absolute transition-all duration-200 ${
                          showSelectedHoverOverlay ? 'group-hover:opacity-0 group-hover:scale-50 group-hover:-rotate-45' : ''
                        }`}
                      />
                      {totalVariantCount > 0 ? (
                        <Pencil
                          size={11}
                          className="absolute opacity-0 scale-50 rotate-45 group-hover:opacity-100 group-hover:scale-100 group-hover:rotate-0 transition-all duration-200"
                        />
                      ) : (
                        hasDetailsModal && (
                          // Info draws its own circle - painted navy (fill and stroke) to blend
                          // into the badge rather than showing as a second, white ring.
                          <Info
                            size={20}
                            className={`absolute [&_circle]:fill-[#0b2c53] [&_circle]:stroke-[#0b2c53] opacity-0 scale-50 rotate-45 group-hover:opacity-100 group-hover:scale-100 group-hover:rotate-0 transition-all duration-200`}
                          />
                        )
                      )}
                    </span>
                  )}
                  {/* Mirrors ProductCard.tsx's (PLP) hover-overlay pattern, but over the whole
                      card rather than just the image - the badge icon swap above (not this
                      label) is what communicates "click to edit" now. */}
                  {showSelectedHoverOverlay && (
                    <div className="absolute -inset-px rounded-lg bg-gray-900/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  )}
                  {product.images[0] ? (
                    <img src={product.images[0]} alt={product.name} className="w-full h-28 object-contain mb-2" />
                  ) : (
                    <div className="w-full h-28 flex items-center justify-center bg-gray-50 text-gray-300 mb-2">
                      <ImageOff size={28} />
                    </div>
                  )}
                  <div ref={observeProductName} data-product-id={product.id} className="text-sm font-medium text-gray-800 line-clamp-2">
                    {product.name}
                  </div>

                  {/* One fixed-height row, its content swapped rather than the row itself
                      mounted/unmounted - picking a variant (closing the modal) must not
                      change this card's height and shove the rest of the grid around. */}
                  {totalVariantCount > 0 && (
                    <div className="mt-1.5 h-5 flex items-center">
                      {isSelected ? (
                        <div className="flex items-center gap-1.5">
                          {canShowSwatches && selectedColor && (
                            <span
                              className="w-3.5 h-3.5 rounded-full border-2 border-gray-300 shadow-sm shrink-0"
                              style={{ backgroundColor: colorHexByName.get(selectedColor) }}
                            />
                          )}
                          <span className="text-xs text-gray-500">{[selectedColor, selectedSize].filter(Boolean).join(' · ')}</span>
                        </div>
                      ) : canShowSwatches ? (
                        // Mirrors ProductCard.tsx's (PLP) color swatch dots - only reachable
                        // here once every color actually has a real hex to paint.
                        <div className="flex items-center gap-1.5">
                          <div className="flex space-x-1">
                            {colors.map((color) => (
                              <span
                                key={color}
                                title={color}
                                className="w-4 h-4 rounded-full border-2 border-gray-300 shadow-sm"
                                style={{ backgroundColor: colorHexByName.get(color) }}
                              />
                            ))}
                          </div>
                          {sizes.length > 0 && (
                            <span className="text-[10px] text-gray-400 font-medium">
                              {sizes.length} size{sizes.length === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-[10px] text-gray-400 font-medium">
                          +{totalVariantCount} option{totalVariantCount === 1 ? '' : 's'}
                        </span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {selectedProduct && (
          <div className="mt-8">
            <SectionHeader title="Where should we send it?" />
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {isSelfClaim && (
                    <Field htmlFor="gift-email" label="Email" className="sm:col-span-2">
                      <input id="gift-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
                    </Field>
                  )}
                  <Field htmlFor="gift-recipient-name" label="Full Name" className="sm:col-span-2">
                    <input id="gift-recipient-name" required value={recipientName} onChange={(e) => setRecipientName(e.target.value)} className={inputClass} />
                  </Field>
                  <Field htmlFor="gift-address1" label="Street Address" className="sm:col-span-2">
                    <input id="gift-address1" required ref={address1Ref} value={address1} onChange={(e) => setAddress1(e.target.value)} className={inputClass} />
                  </Field>
                  <Field htmlFor="gift-address2" label="Apartment, Suite, Unit (optional)" className="sm:col-span-2">
                    <input id="gift-address2" value={address2} onChange={(e) => setAddress2(e.target.value)} className={inputClass} />
                  </Field>
                  <Field htmlFor="gift-city" label="City">
                    <input id="gift-city" required value={city} onChange={(e) => setCity(e.target.value)} className={inputClass} />
                  </Field>
                  <Field htmlFor="gift-state" label="State">
                    <input id="gift-state" required value={state} onChange={(e) => setState(e.target.value)} className={inputClass} />
                  </Field>
                  <Field htmlFor="gift-postalcode" label="ZIP Code">
                    <input id="gift-postalcode" required value={postalcode} onChange={(e) => setPostalcode(e.target.value)} className={inputClass} />
                  </Field>
                  <Field htmlFor="gift-phone" label="Phone Number">
                    <input id="gift-phone" required type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputClass} />
                  </Field>
                </div>
              </div>

              {data.checkoutFields.length > 0 && (
                <OrderInformationSection fields={data.checkoutFields} values={checkoutFieldValues} onChange={setCheckoutFieldValues} />
              )}

              {submitError && <div className="text-sm text-red-600">{submitError}</div>}

              <div className="text-center">
                <button
                  type="submit"
                  disabled={submitting || isPreview}
                  title={isPreview ? 'Preview only - claiming is disabled.' : undefined}
                  className="w-full bg-[#0b2c53] hover:bg-[#0d3563] transition-colors text-white rounded-lg py-3.5 font-medium disabled:opacity-50"
                >
                  {isPreview ? 'Preview Mode' : submitting ? 'Submitting…' : 'Claim My Gift →'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      <div className="text-center text-xs text-gray-400 pb-8">Powered by PromoTrac</div>

      <Modal isOpen={!!variationModalProduct} onClose={() => setVariationModalProduct(null)} size="md">
        {variationModalProduct && (
          <div className="text-center">
            {variationModalProduct.images[0] ? (
              <img src={variationModalProduct.images[0]} alt={variationModalProduct.name} className="w-full h-40 object-contain mx-auto" />
            ) : (
              <div className="w-full h-40 flex items-center justify-center text-gray-300">
                <ImageOff size={36} />
              </div>
            )}

            <h2 className="text-lg font-serif font-semibold text-gray-800 mt-4">{variationModalProduct.name}</h2>
            {variationModalProduct.description && (
              // Rendered as raw HTML, same as ProductPage.tsx's (PDP) description - the admin-authored
              // description carries markup like <br> and PDF links.
              <div
                className="text-sm text-gray-400 mt-1 leading-relaxed [&>p]:mb-4 [&>ul]:list-disc [&>ul]:ml-6 [&>ol]:list-decimal [&>ol]:ml-6 [&>strong]:font-semibold [&>em]:italic [&_a]:text-[#0b2c53] [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: variationModalProduct.description }}
              />
            )}

            {/* Mirrors ProductPage.tsx's (PDP) color-selection buttons: same colored-rectangle
                pill with the color name printed on it via getTextColor(), same '#E5E7EB'
                no-hex fallback - not an invented abbreviation/circle scheme. */}
            {modalColors.length > 0 && (
              <div className="mt-5">
                <div className="text-xs font-medium text-gray-500 mb-2">Color</div>
                <div className="grid grid-cols-3 gap-2">
                  {modalColors.map((color) => {
                    const bgColor = modalColorHexByName.get(color) || '#E5E7EB';
                    const textColor = getTextColor(bgColor);
                    const isChosen = modalColor === color;
                    return (
                      <button
                        type="button"
                        key={color}
                        onClick={() => setModalColor(color)}
                        style={{
                          backgroundColor: bgColor,
                          color: textColor,
                          borderColor: isChosen ? '#0b2c53' : '#D1D5DB',
                        }}
                        className={`py-2 px-3 text-sm border rounded-lg font-medium transition-all ${
                          isChosen ? 'ring-2 ring-[#0b2c53]' : 'hover:border-gray-400'
                        }`}
                        aria-label={`Select ${color} color`}
                        aria-pressed={isChosen}
                      >
                        {color}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {modalSizes.length > 0 && (
              <div className="mt-5">
                <div className="text-xs font-medium text-gray-500 mb-2">Size</div>
                <div className="flex gap-2 flex-wrap justify-center">
                  {modalSizes.map((size) => (
                    <button
                      type="button"
                      key={size}
                      onClick={() => setModalSize(size)}
                      className={`min-w-[2.5rem] px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                        modalSize === size
                          ? 'border-[#0b2c53] bg-[#0b2c53] text-white font-medium'
                          : 'border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Nothing left to confirm for an already-selected gift with no options - the
                modal is purely its details view then. */}
            {!(variationModalProduct.id === selectedProduct?.id && !productHasVariations(variationModalProduct)) && (
              <button
                type="button"
                disabled={modalMissingColor || modalMissingSize}
                onClick={confirmVariationSelection}
                className="mt-6 w-full bg-[#0b2c53] hover:bg-[#0d3563] transition-colors text-white rounded-lg py-3 font-medium disabled:opacity-40"
              >
                Select This Gift
              </button>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

function SectionHeader({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-4">
      <h2 className="text-lg font-serif font-semibold text-gray-800">{title}</h2>
      {subtitle && <p className="text-sm text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
  );
}

function Field({
  htmlFor,
  label,
  hint,
  className = '',
  children,
}: {
  htmlFor: string;
  label: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`flex flex-col ${className}`}>
      <label htmlFor={htmlFor} className="text-xs text-gray-500 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

const inputClass =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

function ConfirmationScreen({
  confirmation,
  recipientName,
}: {
  confirmation: GiftRedemptionConfirmationData;
  recipientName: string;
}) {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex items-center justify-center px-4 py-12 sm:py-20">
      <div className="max-w-md w-full text-center">
        <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <Check className="text-green-600" size={28} />
        </div>
        <h1 className="text-2xl font-serif font-semibold text-gray-800 mb-1">You're all set!</h1>
        <p className="text-gray-500 mb-8">Your gift has been claimed.</p>

        <div className="bg-white border border-gray-200 rounded-lg p-6 text-left space-y-4">
          <div className="flex items-center gap-3">
            {confirmation.gift?.imageUrl && (
              <img
                src={confirmation.gift.imageUrl}
                alt={confirmation.gift.name}
                className="w-16 h-16 object-contain rounded border border-gray-100 shrink-0"
              />
            )}
            <div>
              <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Your Gift</div>
              <div className="font-medium text-gray-800">{confirmation.gift?.name}</div>
              {confirmation.gift?.color && <div className="text-sm text-gray-500">Color: {confirmation.gift.color}</div>}
              {confirmation.gift?.size && <div className="text-sm text-gray-500">Size: {confirmation.gift.size}</div>}
            </div>
          </div>
          <div className="border-t border-gray-100 pt-4">
            <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Shipping To</div>
            <div className="text-sm text-gray-700">{confirmation.shippingAddress.name}</div>
            <div className="text-sm text-gray-700">
              {confirmation.shippingAddress.address1} {confirmation.shippingAddress.address2}
            </div>
            <div className="text-sm text-gray-700">
              {confirmation.shippingAddress.city}, {confirmation.shippingAddress.state} {confirmation.shippingAddress.zip}
            </div>
          </div>
          {confirmation.shipByMessage && (
            <div className="border-t border-gray-100 pt-4 flex items-center gap-2 text-sm text-blue-600">
              <CalendarDays size={14} />
              Expected delivery by {confirmation.shipByMessage}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

function CenteredMessage({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gray-50">
      <p className="text-center text-gray-500 max-w-sm">{children}</p>
    </div>
  );
}
